import type { AxiosError } from "axios";
import Axios from "axios";
import { useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  PrimaryButtonWithPlusIcon,
} from "../../../../../../components/Buttons/Buttons";
import { CurrencyInput } from "../../../../../../components/CurrencyInput/CurrencyInput";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import { endpoints } from "../../../../../../endpoints";
import { Form, FormItems } from "../../../../../../layout/FormLayout";
import type { IDiscountCodesSchema } from "../../../../../../types/types";
import { positiveNumberRegex } from "../../../../../../util/regexes";
import {
  toTitleCase,
  useFormWrapper,
  useStoreState,
} from "../../../../../../util/util";
import {
  ButtonContainer,
  FieldContainer,
  InputRow,
  SaveButtonPrimaryMedium,
} from "../../shared";
import { DatePicker } from "../../../../../../components/DatePicker/DatePicker";
import { SelectBoxV2 } from "../../../../../../components/SelectBoxV2/SelectBoxV2";
import { strings } from "../../../../../../util/strings";
import moment from "moment";

const FormRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 10px;
`;

type DiscountCodeField = {
  id?: string;
  discount_code: string;
  type: { label: string; value: string };
  amount: number;
  valid_from: string;
  expires_on: string;
};

type FormValues = {
  discount_code_fields: DiscountCodeField[] | undefined;
};

export const EditDiscountCodes = ({
  discountCodes,
  mutateDiscountCodes,
  closeEditDiscountCodes,
}: {
  discountCodes: IDiscountCodesSchema[];
  mutateDiscountCodes: () => void;
  closeEditDiscountCodes: () => void;
}) => {
  const { notifySuccess, notifyError } = useNotifications();
  const { storefront_id } = useStoreState();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const methodsOfUseForm = useFormWrapper({
    defaultValues: {
      discount_code_fields:
        discountCodes.length === 0
          ? [
              {
                discount_code: "",
                type: undefined,
                amount: "",
                valid_from: "",
                expires_on: "",
              },
            ]
          : discountCodes.map((item) => {
              return {
                ...item,
                type: { value: item.type, label: toTitleCase(item.type) },
              };
            }),
    },
    mode: "onChange",
  });

  const { register, handleSubmit, errors, control, formState, trigger, watch } =
    methodsOfUseForm;

  const { fields, append, remove } = useFieldArray<DiscountCodeField, "key">({
    control,
    name: "discount_code_fields",
    keyName: "key",
  });
  const codes = watch("discount_code_fields");

  const on_delete_item = async (index: number, discountcodeId?: string) => {
    // if not added in db yet, remove entry
    if (!discountcodeId) {
      remove(index);
      return;
    }

    try {
      setSubmitting(true);
      await Axios.delete(
        endpoints.v2_storefronts_discount_code_delete(
          storefront_id,
          discountcodeId
        )
      );
      mutateDiscountCodes();
      notifySuccess(t("Discount code deleted successfully"));
      setSubmitting(false);
      remove(index);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error deleting discount code"),
        {
          error,
        }
      );
      setSubmitting(false);
    }
  };

  const onSubmit = async (formValues: FormValues) => {
    const is_valid = await trigger();
    const requestBody = formValues.discount_code_fields?.map((item, _) => {
      if (item.id) {
        return discountCodes.find((original) => original.id === item.id);
      }

      delete item.id;
      const formItem = {
        ...item,
        type: item.type.value,
      };
      return formItem;
    });
    if (is_valid) {
      try {
        setSubmitting(true);
        await Axios.put(
          endpoints.v2_storefronts_discount_codes(storefront_id),
          requestBody || []
        );
        mutateDiscountCodes();
        notifySuccess(t("Changes saved"));
        setSubmitting(false);
        closeEditDiscountCodes();
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("There was an error saving the changes"),
          {
            error,
          }
        );
        setSubmitting(false);
      }
    }
  };
  const watchFields = watch("discount_code_fields");

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormItems style={{ maxWidth: "unset" }}>
          {fields.map((field, index) => {
            return (
              <InputRow key={field.key || "formRow" + index}>
                <FieldContainer>
                  <FormRow>
                    <Controller
                      control={control}
                      name={`discount_code_fields[${index}].discount_code`}
                      defaultValue={field.discount_code}
                      render={() => (
                        <TextField
                          label={`Discount Code`}
                          name={`discount_code_fields[${index}].discount_code`}
                          defaultValue={field.discount_code}
                          theref={register({
                            required: true,
                          })}
                          errors={{
                            [`discount_code_fields[${index}].discount_code`]:
                              errors?.discount_code_fields?.[index]
                                ?.discount_code ?? undefined,
                          }}
                          formState={formState}
                          type={"text"}
                          disabled={!!field.id}
                        />
                      )}
                    />
                    <div style={{ minWidth: "150px" }}>
                      <Controller
                        as={SelectBoxV2}
                        control={control}
                        name={`discount_code_fields[${index}].type`}
                        defaultValue={field.type}
                        placeholder={t("Type")}
                        options={[
                          { value: "flat", label: t("Flat") },
                          { value: "percentage", label: t("Percentage") },
                        ]}
                        rules={{ required: strings(t).thisIsARequiredField }}
                        errors={{
                          [`discount_code_fields[${index}].type`]:
                            errors?.discount_code_fields?.[index]?.type ??
                            undefined,
                        }}
                        formState={formState}
                        disabled={!!field.id}
                      />
                    </div>
                    {codes && (
                      <div style={{ maxWidth: "120px" }}>
                        <CurrencyInput
                          name={`discount_code_fields[${index}].amount`}
                          label={`Amount ${
                            codes[index]?.type?.value === "percentage"
                              ? "(%)"
                              : "($)"
                          }`}
                          theref={register({
                            required: true,
                            pattern: {
                              value: positiveNumberRegex,
                              message: t("Must be a valid numeric value"),
                            },
                          })}
                          formState={formState}
                          errors={{
                            [`discount_code_fields[${index}].amount`]:
                              errors?.discount_code_fields?.[index]?.amount ??
                              undefined,
                          }}
                          type="number"
                          defaultValue={field.amount}
                          testid={`discount_code_fields-${index}-amount`}
                          error_bottom_position={
                            errors?.discount_code_fields?.[index]?.amount
                              ?.message === t("Must be a valid numeric value")
                              ? "-22px"
                              : undefined
                          }
                          disabled={!!field.id}
                        />
                      </div>
                    )}
                    <DatePicker
                      label={t(`Valid From`)}
                      name={`discount_code_fields[${index}].valid_from`}
                      defaultValue={field.valid_from}
                      required={true}
                      methodsOfUseForm={methodsOfUseForm}
                      isOutsideRange={(day) =>
                        watchFields[index].expires_on
                          ? day.isSameOrAfter(watchFields[index].expires_on)
                          : false
                      }
                      disabled={!!field.id}
                      rules={{
                        required: t("This is a required field"),
                      }}
                    />
                    <DatePicker
                      label={t(`Expires On`)}
                      name={`discount_code_fields[${index}].expires_on`}
                      defaultValue={field.expires_on}
                      required={true}
                      methodsOfUseForm={methodsOfUseForm}
                      isOutsideRange={(day) => {
                        const validFrom = watchFields[index].valid_from
                          ? moment(watchFields[index].valid_from)
                          : null;
                        const today = moment();

                        const minDate =
                          validFrom && validFrom.isAfter(today)
                            ? validFrom
                            : today;

                        return day.isSameOrBefore(minDate);
                      }}
                      disabled={!!field.id}
                      rules={{
                        required: t("This is a required field"),
                      }}
                    />

                    <input
                      name={`discount_code_fields[${index}].id`}
                      defaultValue={field.id}
                      ref={register({ required: false })}
                      type={"hidden"}
                    />
                  </FormRow>
                </FieldContainer>
                <ButtonContainer>
                  <ButtonWithConfirmDialog
                    Button={DeleteButton}
                    confirmMessage={
                      "Are you sure you want to remove this item?"
                    }
                    handleConfirm={() => on_delete_item(index, field.id)}
                    type="button"
                    testid={`delete-fees-and-charges-field-${index}`}
                  />
                </ButtonContainer>
              </InputRow>
            );
          })}
        </FormItems>
        <div>
          <PrimaryButtonWithPlusIcon
            type="button"
            onClick={() => {
              append({
                discount_code: "",
                type: undefined,
                amount: undefined,
                valid_from: "",
                expires_on: "",
              });
            }}
          >
            {t("Add")}
          </PrimaryButtonWithPlusIcon>
        </div>
        <SaveButtonPrimaryMedium type="submit" loading={submitting}>
          {t("Save your changes")}
        </SaveButtonPrimaryMedium>
      </Form>
    </>
  );
};
